import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, PageLoader, StatusBadge } from "@/components/shared";
import { CreditCard, ExternalLink, FileText } from "lucide-react";

export default function Billing() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/billing/subscription").then((r) => setData(r.data)); }, []);
  if (!data) return <PageLoader />;

  const portal = async () => {
    try { const { data: d } = await api.post("/billing/portal", { return_url: window.location.href }); window.location.href = d.url; }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2"><CreditCard className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Billing</h1></div>

      <div className="grid gap-5 lg:grid-cols-2">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-slate-100">Subscription</h2>
            <StatusBadge status={data.billing_status} />
          </div>
          {data.plan ? (
            <div className="mt-4">
              <div className="font-display text-2xl font-bold text-slate-50">{data.plan.name}</div>
              <div className="font-mono text-sm text-cyan-300">{data.plan.storage_tb} TB · ${data.plan.price_monthly}/mo</div>
              <button onClick={portal} data-testid="manage-billing-btn" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] hover:bg-cyan-300">
                Manage in Stripe Portal <ExternalLink className="h-4 w-4" />
              </button>
              <p className="mt-2 text-xs text-slate-500">Update payment method, download invoices, or change plan.</p>
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-400">No active subscription. Deploy a server to get started.</p>
          )}
        </GlassCard>

        <GlassCard className="p-6">
          <h2 className="mb-3 font-display font-semibold text-slate-100">Invoices</h2>
          {data.invoices.length === 0 ? (
            <p className="text-sm text-slate-500">No invoices yet.</p>
          ) : (
            <div className="space-y-2">
              {data.invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#06080D] p-3 text-sm">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-500" /><span className="text-slate-300">{i.number || i.id.slice(-8)}</span></div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-slate-200">${i.amount}</span>
                    <span className={i.status === "paid" ? "text-emerald-400" : "text-amber-400"}>{i.status}</span>
                    {i.pdf && <a href={i.pdf} target="_blank" rel="noreferrer" className="text-cyan-300"><ExternalLink className="h-4 w-4" /></a>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>

      {data.billing_status === "past_due" && (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200">
          Your last payment failed. Please update your payment method — we won't delete backups. Administrators control final suspension policy after the grace period.
        </div>
      )}
    </div>
  );
}
